/**
 * Type Definitions for Appraisal 360 Application
 * 
 * This file contains all TypeScript interfaces and type definitions
 * used throughout the application for type safety and better development experience.
 */

/**
 * User Interface
 * 
 * Represents a user in the system (employee who can perform appraisals)
 */
export interface User {
  id: string;              // Unique user identifier
  name: string;            // Full name of the user
  email: string;           // Email address for authentication
  designation: string;     // Job title/position
  department: string;      // Department/team the user belongs to
  managerId?: string;      // Optional manager's user ID
  avatar?: string;         // Optional profile picture URL
  name_of_employee?: string; // API: Full name of the employee
  employee_id?: string;      // API: Employee ID
  reporting_manager?: string; // API: Reporting manager
  reporting_manager_email?: string; // API: Reporting manager email
  Contact_no?: string;        // API: Contact number
  timezone?: string;          // API: Timezone
  language_preference?: string; // API: Language preference 
  country?:string;
}

/**
 * Employee Interface
 * 
 * Represents an employee in the organization who can be reviewed/nominated
 */
export interface Employee {
  id: string;              // Unique employee identifier
  employeeId: string;      // Employee ID/number in company system
  name: string;            // Full name of the employee
  designation: string;     // Job title/position
  department: string;      // Department/team the employee belongs to
  email: string;           // Employee's email address
  avatar?: string;         // Optional profile picture URL
}

/**
 * Nomination Review Interface
 * 
 * Represents a nomination for 360-degree feedback review
 */
export interface NominationReview {
  id: string;                                           // Unique nomination ID
  employeeId: string;                                   // ID of employee being reviewed
  employee: Employee;                                   // Full employee details
  nominationType: 'Peer' | 'Subordinate' | 'Additional Subordinate' | 'Superior'; // Type of relationship
  status: 'Pending' | 'Completed' | 'In Progress';     // Current review status
  submittedAt?: Date;                                   // When review was submitted
  reviewedBy: string;                                   // ID of person conducting review
  reportingManager: string;                             // Manager overseeing the review
}

/**
 * Rating Criteria Interface
 * 
 * Represents individual criteria used in performance evaluation
 */
export interface RatingCriteria {
  id: number;                               // Unique criteria identifier
  description: string;                      // Description of the criteria
  rating: 1 | 2 | 3 | 4 | null;           // Rating scale: 1-Excel, 2-Fully Met, 3-Mostly Met, 4-Requires Action
}

/**
 * Feedback Form Interface
 * 
 * Represents a feedback form for 360-degree reviews
 */
export interface FeedbackForm {
  id: string;                    // Unique form identifier
  employeeId: string;            // ID of employee being reviewed
  reviewerId: string;            // ID of person providing feedback
  criteria: RatingCriteria[];    // Array of rating criteria with scores
  appreciativeFeedback: string;  // Positive feedback comments
  constructiveFeedback: string;  // Areas for improvement comments
  status: 'Draft' | 'Submitted'; // Current form status
  submittedAt?: Date;            // When form was submitted
}

/**
 * Objective Interface
 * 
 * Represents individual objectives in self-appraisal
 */
export interface Objective {
  id: number;           // Unique objective identifier
  objective: string;    // Description of the objective
  target: string;       // Target/goal to be achieved
  endYearResult: string; // Actual result achieved
  rating: number;       // Self-rated performance score
}

/**
 * Self Appraisal Interface
 * 
 * Represents a complete self-appraisal form submitted by an employee
 */
export interface SelfAppraisal {
  id: string;                    // Unique self-appraisal identifier
  employeeId: string;            // ID of employee submitting appraisal
  objectives: Objective[];       // Array of objectives and their ratings
  status: 'Draft' | 'Submitted'; // Current appraisal status
  submittedAt?: Date;            // When appraisal was submitted
}

/**
 * Nomination Interface
 * 
 * Represents nominations made by an employee for 360-degree feedback
 */
export interface Nomination {
  id: string;                    // Unique nomination identifier
  employeeId: string;            // ID of employee making nominations
  nominationApprover: string;    // ID of person who approves nominations
  superiors: string[];           // Array of superior employee IDs
  subordinates: string[];        // Array of subordinate employee IDs
  peers: string[];               // Array of peer employee IDs
  stakeholders: string[];        // Array of stakeholder employee IDs
  status: 'Draft' | 'Submitted'; // Current nomination status
  submittedAt?: Date;            // When nominations were submitted
}

/**
 * Appraisal Cycle Interface
 * 
 * Represents an appraisal cycle/period in the organization
 */
export interface AppraisalCycle {
  id: number;                                              // Unique cycle identifier
  name: string;                                            // Cycle name (e.g., "Year-2026")
  start_date: string;                                      // Cycle start date (ISO string)
  end_date: string;                                        // Cycle end date (ISO string)
  self_appraisal_deadline: string;                         // Deadline for self appraisal submission
  okr_approval_deadline: string;                           // Deadline for OKR approval
  nomination_deadline: string;                             // Deadline for nominations
  nomination_approval_deadline: string;                    // Deadline for nomination approvals
  review_deadline: string;                                 // Deadline for peer/team reviews
  hod_review_deadline: string;                             // Deadline for HOD reviews
  status: 'draft' | 'active' | 'completed' | 'archived';  // Current cycle status
  disabled: boolean;                                       // Whether the cycle is disabled
  cycle_type: CycleType;                                   // Phase of the cycle: START(0), MID(1), END(2)
}

/**
 * Cycle Type Enum
 * 
 * Represents the phase of an appraisal cycle
 */
export const CycleType = {
  START: 0,
  MID: 1,
  END: 2,
} as const;

export type CycleType = typeof CycleType[keyof typeof CycleType];

/**
 * Navigation Tab Type
 * 
 * Defines the available navigation tabs in the application
 */
export type NavigationTab = 'dashboard' | 'self-appraisal' | 'nomination-review' | 'nomination-approval' | 'team-review' | 'hod-review' | 'profile';

/**
 * API Response Nominator Interface
 * 
 * Represents a nominator in the API response structure
 */
export interface ApiNominator {
  nominator_id: number;
  nominator_name: string;
  employee_id: string;
  nomination_relationship_id: number;
  nominator_designation?: string;
  nominator_department?: string;
  review_exists?: boolean;
  relationship_type?: string;
  nominator_email?: string;
  nominator_manager?: {
    manager_name: string;
    manager_id?: string;
    manager_email?: string;
  };
}

/**
 * API Nomination Response Interface
 * 
 * Represents the structure of nomination review API responses
 */
export interface ApiNominationResponse {
  nominators: ApiNominator[];
  status?: boolean;
  message?: string;
}

/**
 * API Error Interface
 * 
 * Represents error responses from the API
 */
export interface ApiError {
  message: string;
  status?: number;
  details?: string;
}
